import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./i18n";

import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router-dom";
import reportWebVitals from "./reportWebVitals";
import Reset from "./pages/Reset";
import ResetConfirm from "./pages/ResetConfirm";
import Login from "./pages/Login";
import TenantSelect from "./pages/TenantSelect";
import Dashboard from "./pages/Dashboard";
import ApplicationDetail from "./pages/ApplicationDetail";
import Applications from "./pages/Applications";
import TenantDetail from "./pages/TenantDetail";
import OrganisationDetail from "./pages/OrganisationDetail";
import Organisations from "./pages/Organisations";
import UserManagement from "./pages/UserManagement";
import Users from "./pages/Users";
import Logs from "./pages/Logs";
import Roles from "./pages/Roles";
import Settings from "./pages/Settings";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ThemeProvider } from "@emotion/react";
import { createTheme, CssBaseline } from "@mui/material";
import LoginLayout from "./components/login/Layout";
import AppLayout from "./components/layout/AppLayout";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { TenantProvider } from "./hooks/useTenant";

function RequireAuth() {
  const { user, loading } = useAuth();

  if (loading) return null;

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <TenantProvider>
      <AppLayout />
    </TenantProvider>
  );
}

function AuthRoot() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <AuthRoot />,
    children: [
      {
        path: "/",
        element: <LoginLayout />,
        children: [
          { path: "", element: <Login /> },
          { path: "reset", element: <Reset /> },
          { path: "reset-password", element: <ResetConfirm /> },
        ],
      },
      { path: "/select-tenant", element: <TenantSelect /> },
      {
        path: "/app",
        element: <RequireAuth />,
        children: [
          { path: "", element: <Dashboard /> },
          { path: "tenants/:id", element: <TenantDetail /> },
          { path: "tenants/:id/users", element: <UserManagement /> },
          { path: "applications", element: <Applications /> },
          { path: "applications/:id", element: <Navigate to="clients" replace /> },
          { path: "applications/:id/clients", element: <ApplicationDetail /> },
          { path: "applications/:id/permissions", element: <ApplicationDetail /> },
          { path: "applications/:id/users", element: <ApplicationDetail /> },
          { path: "users", element: <Users /> },
          { path: "roles", element: <Roles /> },
          { path: "logs", element: <Logs /> },
          { path: "settings", element: <Settings /> },
          { path: "organisations", element: <Organisations /> },
          { path: "organisations/:id", element: <OrganisationDetail /> },
        ],
      },
    ],
  },
]);

const darkTheme = createTheme({
  palette: {
    mode: "dark",
  },
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <React.StrictMode>
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID as string}>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <RouterProvider router={router} />
      </ThemeProvider>
    </GoogleOAuthProvider>
  </React.StrictMode>
);

reportWebVitals();
